/**
 * OrPanel
 * -------
 * 
 * August 2026
 */

import * as React from 'react';

import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from "primereact/fileupload";
import { InputNumber } from 'primereact/inputnumber';
import { MultiSelect } from 'primereact/multiselect';

const orWarnings = {
    'probability_script': function(newProbs) { return newProbs.reduce((total, currentProb) => total + currentProb, 0) !== 100 ? "Probabilities must sum to 100" : null }
}

/**
 * ActionPanel sub-component for editing a OR element's attributes.
 * 
 * @param {Object} config                   - Configuration object for the simulation model. 
 * @param {Function} setConfig              - Setter for config object.
 * @param {Object} action                   - Attributes for action element being edited.
 * @param {Object} configWarnings           - Verification warnings for entity types.
 * @param {Function} updateActionWarnings   - Setter for verification warnings.
 * @param {Boolean} reachedConfig           - Flag for when users enter the configuration panel.
 */
function OrPanel({ config, setConfig, action, configWarnings, updateActionWarnings, reachedConfig }) {
    if (action.type !== 'or') return null;
    
    const fileUploadRef = React.useRef(null);

    /**
     * Memo hook tracks the entity types assignable to each decision branch.
     * Entity types already assigned are excluded from other decision branches.
     *
     */
    const entityTypeOptions = React.useMemo(() => {
        if (action.type !== 'or' || action.or_script.type !== 'entity_type') return null;

        const allEntityTypes = Object.values(config.entity_types);
        const selectedGids = new Set(action.or_script.decision.flat());

        return action.or_script.decision.map(branch => {
            const branchSelected = new Set(branch);

            return allEntityTypes.filter(entityType =>
                branchSelected.has(entityType.gid) || !selectedGids.has(entityType.gid)
            );
        });
    }, [config.entity_types, action.type, action.or_script]);

    /**
     * Updates the "OR" element's script for deciding which branch to process.
     * Available scripts:
     *  - Probability
     *  - Entity type
     * 
     * @param {String} newScript    - Updated script being set.
     * @param {String} gid          - Action's identifier.
     */
    const handleOrScript = (newScript, gid) => {
        updateActionWarnings(gid, 'probability_script', null);

        let newDecision = null;
        switch (newScript) {
            case "probability":
                newDecision = Array(action.branch_labels.length).fill(0);
                updateActionWarnings(gid, 'probability_script', "Probabilities must sum to 100");
                break;
            case "entity_type":
                newDecision = Array(action.branch_labels.length).fill([]);
                break;
            case "lookup":
                newDecision = {
                    "input_var": {"element": "action", "gid": "", "var": ""},
                    "input_vals": [],
                    "probabilities": []
                };
                break;
        }

        const newOrScript = {'type': newScript, 'decision': newDecision};
        const newAction = {...config.actions[gid], 'or_script': newOrScript};
        const newActions = {...config.actions, [gid]: newAction};
        const newConfig = {...config, actions: newActions};
        setConfig(newConfig);
    }

    /**
     * Updates the or element's probability script.
     * 
     * @param {Number} newProb  - Updated probability value.
     * @param {String} gid      - Action's identifier.
     * @param {Number} i        - Probability branch index
     */
    const handleOrProbability = (newProb, gid, i) => {
        let newProbabilities = [...config.actions[gid].or_script.decision];
        newProbabilities[i] = newProb;

        updateActionWarnings(gid, 'probability_script', orWarnings['probability_script'](newProbabilities));

        const newScript = {'type': 'probability', 'decision': newProbabilities};
        const newAction = {...config.actions[gid], 'or_script': newScript};
        const newActions = {...config.actions, [gid]: newAction};
        const newConfig = {...config, actions: newActions};
        setConfig(newConfig);
    }

    /**
     * Updates the entity types assigned to an "OR" branch.
     * 
     * @param {Array} newAssignment - Updated entity assignment.
     * @param {String} gid          - Action's identifier.
     * @param {Number} i            - Decision branch index.
     */
    const handleEntityTypeAssignment = (newAssignment, gid, i) => {
        let newDecision = [...config.actions[gid].or_script.decision];
        newDecision[i] = newAssignment;

        const newScript = {'type': 'entity_type', 'decision': newDecision};
        const newAction = {...config.actions[gid], 'or_script': newScript};
        const newActions = {...config.actions, [gid]: newAction};
        const newConfig = {...config, actions: newActions};
        setConfig(newConfig);
    }

    /**
     * 
     * @param {Object} event    - JSON file being uploaded. 
     */
    const parseLookupData = async (event, gid) => {
        const file = event.files[0];
        const text = await file.text();
        const data = JSON.parse(text);

        const newDecision = data;
        const newScript = {"type": "lookup", "decision": newDecision};
        const newAction = {...config.actions[gid], "or_script": newScript};
        const newActions = {...config.actions, [gid]: newAction};
        const newConfig = {...config, actions: newActions};
        setConfig(newConfig);

        console.log(newScript);

        fileUploadRef.current?.clear();
    }

    return (
        <div>
            <div style={{ marginBottom: '1rem'}}>
                <label style={{ marginRight: '0.5rem'}}>"OR" Script:</label>
                <Dropdown value={action.or_script.type} onChange={(event) => handleOrScript(event.target.value, action.gid)} options={["probability","entity_type", "lookup"]} />
            </div>
            {action.or_script.type === 'probability' && (
                <div>
                    {action.or_script.decision.map((prob, i) => (
                        <div key={i} style={{ marginTop: '0.5rem' }}>
                            <label style={{ marginRight: '1rem' }}>Branch "{action.branch_labels[i]}"</label>
                            <InputNumber value={prob} onValueChange={(event) => handleOrProbability(event.target.value, action.gid, i)} placeholder="Enter value" min={0} useGrouping={false} invalid={!!configWarnings.actions?.[action.gid]?.probability_script} inputStyle={{ width: '110px' }}/>
                        </div>
                    ))}
                    <label>Values must be between 0 and 100.</label>
                </div>
            )}
            {action.or_script.type === 'entity_type' && (
                <div>
                    {action.or_script.decision.map((branch, i) => (
                        <div key={i} style={{ marginTop: '0.5rem' }}>
                            <label style={{ marginRight: '1rem' }}>Branch "{action.branch_labels[i]}"</label>
                            <MultiSelect value={branch} options={entityTypeOptions[i]} optionValue='gid' optionLabel='name' onChange={(event) => handleEntityTypeAssignment(event.value, action.gid, i)}/>
                        </div>
                    ))}
                </div>
            )}
            {action.or_script.type === "lookup" && (
                <div>
                    <FileUpload
                        ref={fileUploadRef}
                        mode="basic"
                        auto
                        customUpload
                        onSelect={(event) => parseLookupData(event, action.gid)}
                        chooseLabel="Upload Lookup Data"
                        accept=".json"
                        maxFileSize={1000000}
                    />
                </div>
            )}
        </div>
    )
}

export default OrPanel;